import { PricingTypeId } from "constants/pricingTypes";
import { Big } from "big.js";

type HdPricingFields = Pick<
  SaleLot & { pricing_type_id: 1 },
  "quantity" | "total_price_cents" | "pricing_type_id"
>;
type MassPricingFields = Pick<
  SaleLot & { pricing_type_id: 2 },
  "total_mass_grams" | "total_price_cents" | "pricing_type_id"
>;
type GrossPricingFields = Pick<
  SaleLot & { pricing_type_id: 3 },
  "total_price_cents" | "pricing_type_id"
>;

type SaleLotPricingFields =
  | GrossPricingFields
  | MassPricingFields
  | HdPricingFields;

function isTotalPriceStoringUnitPrice(
  totalPriceCents: number,
  unitScalar: number,
): boolean {
  return totalPriceCents > 0 && unitScalar === 0;
}

function preciseCentsPerHead(pricingFields: HdPricingFields): number {
  if (
    isTotalPriceStoringUnitPrice(
      pricingFields.total_price_cents,
      pricingFields.quantity,
    )
  ) {
    return pricingFields.total_price_cents;
  }
  if (pricingFields.quantity === 0) {
    // Don't divide by zero
    return 0;
  }

  return pricingFields.total_price_cents / pricingFields.quantity;
}

function roundCentsPerHead(pricingFields: HdPricingFields): number {
  return Math.round(preciseCentsPerHead(pricingFields));
}

function centsPerKilo(pricingFields: MassPricingFields): number {
  if (
    isTotalPriceStoringUnitPrice(
      pricingFields.total_price_cents,
      pricingFields.total_mass_grams,
    )
  ) {
    return pricingFields.total_price_cents;
  }
  if (pricingFields.total_mass_grams === 0) {
    // Don't divide by zero
    return 0;
  }

  return (
    (pricingFields.total_price_cents / pricingFields.total_mass_grams) * 1000
  );
}

function unitPriceCents(pricingFields: SaleLotPricingFields): number {
  if (pricingFields.pricing_type_id === PricingTypeId.PER_HEAD) {
    return roundCentsPerHead(pricingFields as HdPricingFields);
  }

  if (pricingFields.pricing_type_id === PricingTypeId.GROSS) {
    return pricingFields.total_price_cents;
  }
  return 0;
}

/**
 * Returns a homogenised whole number unit price to be used as a checksum for any specific Sale Lot
 * Returns the c/kg for Sale Lots sold as c/kg
 * Returns total sale value in $ value for Sale Lots sold as Gross
 * Returns $/hh value for Sale Lots sold as as $/hd
 *
 * Working on the approximate figures:
 *  - c/kg lots will be sold from 100 - 600 c/kg (e.g cattle)
 *  - $/hd lots will be sold from 50 - 300 $/hd (e.g. sheep)
 *  - $/ea lots will be sold from $30 - $5000 (e.g. stud cattle, or an ATV)
 *
 *  This checksum will likely return between the 50-5000 for any given (sold lot)
 *  and 0 when not sold, or when parameters are supplied
 */
export function getHashTotal(pricingFields: SaleLotPricingFields): Big {
  if (!pricingFields) {
    // This lot does not contribute to the hash total
    return Big(0);
  }

  if (pricingFields.pricing_type_id === PricingTypeId.PER_KILO) {
    return Big(centsPerKilo(pricingFields as MassPricingFields)).round(2);
  }

  return new Big(unitPriceCents(pricingFields)).div(100).round(2);
}
